import { Button, Stack, Typography } from '@mui/joy';
import { useEffect } from 'react';
import { useRouteError } from 'react-router-dom';

import Loading from '@MP/shared/components/Loading';

/** 이미 새로고침을 시도한 모듈. 같은 모듈로 또 실패하면(서버에서 정말 사라진 경우) 무한 새로고침이 된다. */
const RELOADED_KEY = 'moduleLoadReloaded';

/**
 * 배포로 청크 파일명이 바뀌어 오래 열어둔 탭이 옛 청크를 못 받는 상황.
 * webpack 은 ChunkLoadError 라는 이름을 붙이지만 Vite 는 그냥 TypeError 로 던져서 메시지까지 봐야 한다.
 */
const moduleLoadSignature = (error: unknown): string | undefined => {
  if (!(error instanceof Error)) {
    return undefined;
  }
  if (error.name === 'ChunkLoadError') {
    return error.message;
  }
  return /Failed to fetch dynamically imported module|Importing a module script failed|error loading dynamically imported module/i.test(
    error.message
  )
    ? error.message
    : undefined;
};

const readReloaded = (): string | null => {
  try {
    return window.sessionStorage.getItem(RELOADED_KEY);
  } catch {
    return null;
  }
};

const markReloaded = (signature: string) => {
  try {
    window.sessionStorage.setItem(RELOADED_KEY, signature);
  } catch {
    // 저장이 막힌 브라우저(사생활 보호 모드 등)에서는 무한 새로고침 방지를 포기하고 복구를 우선한다.
  }
};

/** 복구할 수 없는 에러 안내. 로딩 스피너로 그리면 사용자가 영원히 기다리게 된다. */
const ErrorNotice = () => (
  <Stack height="100vh" justifyContent="center" alignItems="center" spacing="16px">
    <Typography level="title-lg">일시적인 오류가 발생했습니다</Typography>
    <Typography level="body-md" sx={{ color: 'text.secondary' }}>
      새로고침 후 다시 시도해 주세요. 문제가 계속되면 고객센터로 문의해 주세요.
    </Typography>
    <Button onClick={() => window.location.reload()}>새로고침</Button>
  </Stack>
);

/**
 * 라우트 에러 화면.
 * 배포로 청크가 교체돼 실패한 경우에만 한 번 새로고침해 새 배포본을 받아오고, 그 밖의 에러는 안내를 보여준다.
 */
export function ErrorBoundary() {
  const error = useRouteError();
  const signature = moduleLoadSignature(error);
  const reloading = signature !== undefined && readReloaded() !== signature;

  useEffect(() => {
    if (reloading && signature !== undefined) {
      markReloaded(signature);
      window.location.reload();
    }
  }, [reloading, signature]);

  // 곧 새로고침되므로 안내를 깜빡이지 않고 로딩을 유지한다.
  return reloading ? <Loading /> : <ErrorNotice />;
}
